import { ReactElement, memo, useState } from 'react';
import { Heading } from '@appsumo/dorado-react';

import { useCart } from '~/lib/cart';
import {
  ComingSoonCouponInfoType,
  CouponType,
  DiscountDataType,
} from '~/types/account';
import { CartProps } from '~/types/cart';

import {
  CouponDisplay,
  AutoApplyCouponWarning,
} from '~/components/account/Discounts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';

const MAX_VISIBLE_COUPONS = 2;

const CouponDisplays = memo(
  ({ coupons, couponId }: { coupons: CouponType[]; couponId: number }) => {
    const [showMore, setShowMore] = useState(false);

    return (
      <div className="flex flex-col gap-4">
        {coupons?.map((coupon: CouponType, index: number) => (
          <CouponDisplay
            className={
              index >= MAX_VISIBLE_COUPONS && !showMore ? 'hidden' : ''
            }
            key={coupon.id}
            coupon={coupon}
            isCurrentCouponApplied={couponId === coupon.id}
          />
        ))}

        {/* View more button */}
        {coupons.length > MAX_VISIBLE_COUPONS && (
          <div className="text-right">
            <button
              onClick={() => setShowMore(!showMore)}
              className="text-bolt"
            >
              <FontAwesomeIcon
                icon={showMore ? faChevronUp : faChevronDown}
                className="mr-2"
              />
              {showMore
                ? 'View less'
                : `View more (${coupons.length - MAX_VISIBLE_COUPONS})`}
            </button>
          </div>
        )}
      </div>
    );
  },
);
CouponDisplays.displayName = 'CouponDisplays';

export default function AccountCoupons({
  discountData,
  initialCart,
}: Readonly<{
  discountData?: DiscountDataType;
  initialCart?: CartProps;
}>): ReactElement {
  const { cart } = useCart(initialCart);
  const { coupons: activeCoupons, coming_soon_coupons: comingSoonCoupons } =
    discountData ?? {};
  const coupons: CouponType[] = [
    ...(activeCoupons || []),
    ...(comingSoonCoupons?.map((x: ComingSoonCouponInfoType) => ({
      ...x,
      isComingSoon: true,
    })) || []),
  ];

  return (
    <div className="mb-4 border-b border-sundog pb-4">
      <Heading.H3 className="mb-2">Coupons</Heading.H3>
      {!coupons?.length ? (
        <p>You don&apos;t have any active coupons.</p>
      ) : (
        <>
          {!!cart?.active_auto_campaign_coupon_applied && (
            <div className="mb-4">
              <p className="mb-4">
                Available coupons can be added to your cart at any time, however
                they cannot be stacked with any other coupons or promotional
                discounts. Applying a coupon to cart will override any
                previously applied discount.
              </p>
              <AutoApplyCouponWarning couponDesc={cart.coupon_desc} />
            </div>
          )}
          <CouponDisplays
            coupons={coupons}
            couponId={cart?.coupon?.coupon_id}
          />
        </>
      )}
    </div>
  );
}

AccountCoupons.displayName = 'AccountCoupons';
